import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Plus, Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { COARSE_HIT_32 } from '@/lib/touch-target';
import { useTranslation } from 'react-i18next';

/**
 * PageToolbar — the one icon toolbar under a page headline (Health declutter
 * 2026-09-09, option D; platform-wide 2026-09-10, DECISIONS).
 *
 * Order, left to right: `+` · search · filter · separator · trailing tools.
 * Every button on a page that adds, finds, narrows, prints or exports lives
 * here; nothing in it is a number. Rules the primitive owns so pages cannot
 * drift on them:
 *   - `+` runs its single item directly, or opens a menu when there are
 *     several, so no create action requires switching section first (#848).
 *   - Search is hidden until asked. Opening focuses the field; Escape and the
 *     × close it AND clear the query, so a list never filters invisibly.
 *   - Filter is a popover; `active` paints the indigo dot on the icon.
 *   - `end` is for controls that are not tools over the list (Calendar's
 *     date navigation and view switch) — right-aligned, wraps under on a phone.
 */
export interface ToolbarAddItem {
  label: string;
  onSelect: () => void;
}

export interface PageToolbarProps {
  /** Names the landmark: `aria-label="{label} tools"`. */
  label: string;
  add?: {
    items: ToolbarAddItem[];
    /** Extra attributes on the + button (onboarding guide hooks: `data-guide`, `data-spotlight`). */
    buttonProps?: Record<string, string>;
    className?: string;
  };
  search?: {
    value: string;
    onChange: (q: string) => void;
    placeholder: string;
  };
  filter?: {
    active: boolean;
    panel: ReactNode;
    /** Names the popover: `aria-label={label}`. */
    label: string;
  };
  /** Trailing tools (print, export…), rendered after a separator. */
  children?: ReactNode;
  /** Right-aligned controls that are not tools over the list. */
  end?: ReactNode;
  className?: string;
}

/**
 * #2494 (D-2420): the icons draw at 32 px on every pointer; on a coarse
 * pointer an invisible ::after reaches 6 px past each edge so each owns a
 * 44 px square. The toolbar row is their `isolate` context and spaces them
 * 12 px apart on touch (twice the reach), so neighbouring areas meet edge to
 * edge and never overlap.
 */
const ICON_BTN = cn('h-8 w-8 p-0 text-stevens-ink-2 hover:text-foreground', COARSE_HIT_32);
/**
 * The `+` is an outline button: its 1 px border leaves a 30 px padding box,
 * which is what the ::after insets from, so it reaches 7 px → 44 px.
 */
const ADD_BTN = cn(
  'h-8 w-8 p-0 text-stevens-indigo hover:text-stevens-indigo',
  COARSE_HIT_32,
  '[@media(pointer:coarse)]:after:-inset-[7px]',
);

function ActiveDot() {
  return (
    <span
      aria-hidden
      className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-stevens-indigo"
    />
  );
}

/**
 * #2490: a toolbar tooltip opens on a fine pointer (a mouse) only. Radix opens
 * a tooltip on focus, and a tap focuses the button, so on a phone the label
 * stayed up after the tap, over the line under the toolbar, until something
 * else took focus. On touch the button's accessible name already carries the
 * label, so no tooltip is mounted at all. Where `matchMedia` is unavailable
 * (tests) the pointer is taken as fine, which is today's behaviour.
 */
const FINE_POINTER = '(pointer: fine)';

function useFinePointer(): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [fine, setFine] = useState(() => !supported || window.matchMedia(FINE_POINTER).matches);
  useEffect(() => {
    if (!supported) return;
    const mql = window.matchMedia(FINE_POINTER);
    const onChange = (e: MediaQueryListEvent) => setFine(e.matches);
    setFine(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [supported]);
  return fine;
}

/** Wraps `children` (the trigger) in a bottom tooltip on a fine pointer; on touch, renders it bare. */
function ToolbarTip({ label, children }: { label: string; children: ReactNode }) {
  const fine = useFinePointer();
  if (!fine) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

export function ToolbarIconButton({
  label,
  onClick,
  children,
  active,
  disabled,
  className,
}: {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  active?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <ToolbarTip label={label}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        data-active={active ? 'true' : undefined}
        className={cn(ICON_BTN, className)}
      >
        {children}
        {active && <ActiveDot />}
      </Button>
    </ToolbarTip>
  );
}

export function ToolbarSeparator() {
  return <span aria-hidden className="mx-1 h-4 w-px bg-border" />;
}

export function PageToolbar({ label, add, search, filter, children, end, className }: PageToolbarProps) {
  // #2109 R3 fix pass: the toolbar's own chrome is `common:toolbar.*`; the
  // caller's `label` is already translated and slots into `tools`.
  const { t } = useTranslation('common');
  const [searchOpen, setSearchOpen] = useState((search?.value.length ?? 0) > 0);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  const closeSearch = () => {
    setSearchOpen(false);
    search?.onChange('');
  };

  const addButton = add && (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label={t('toolbar.add')}
      className={cn(ADD_BTN, add.className)}
      onClick={add.items.length === 1 ? add.items[0].onSelect : undefined}
      {...add.buttonProps}
    >
      <Plus className="h-4 w-4" />
    </Button>
  );

  return (
    <TooltipProvider delayDuration={300}>
      {/* #2885: when `end` wraps under the toolbar on a phone, a 16 px row gap
          keeps the toolbar's 6 px reach below and an end control's 8 px reach
          above (the calendar arrows) from meeting. */}
      <div className={cn('flex flex-wrap items-center gap-2 [@media(pointer:coarse)]:gap-y-4', className)}>
        <div role="toolbar" aria-label={t('toolbar.tools', { label })} className="isolate inline-flex items-center gap-0.5 [@media(pointer:coarse)]:gap-3">
          {add && add.items.length === 1 && (
            <ToolbarTip label={add.items[0].label}>{addButton}</ToolbarTip>
          )}
          {add && add.items.length > 1 && (
            <DropdownMenu>
              <ToolbarTip label={t('toolbar.add')}>
                <DropdownMenuTrigger asChild>{addButton}</DropdownMenuTrigger>
              </ToolbarTip>
              <DropdownMenuContent align="start">
                {add.items.map((item) => (
                  <DropdownMenuItem key={item.label} onClick={item.onSelect}>
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {search && (
            <ToolbarIconButton
              label={t('toolbar.search')}
              active={search.value.length > 0}
              onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
            >
              <Search className="h-4 w-4" />
            </ToolbarIconButton>
          )}

          {filter && (
            <Popover>
              <ToolbarTip label={t('toolbar.filter')}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={t('toolbar.filter')}
                    data-active={filter.active ? 'true' : undefined}
                    className={ICON_BTN}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {filter.active && <ActiveDot />}
                  </Button>
                </PopoverTrigger>
              </ToolbarTip>
              <PopoverContent
                align="start"
                aria-label={filter.label}
                // #2408: on a phone the panel must fit the viewport. Keep 16px
                // off every edge, cap the height at the space Radix measures,
                // scroll inside it, and never grow wider than the screen.
                collisionPadding={16}
                className="w-80 max-w-[calc(100vw-2rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto space-y-4"
              >
                {filter.panel}
              </PopoverContent>
            </Popover>
          )}

          {children && <ToolbarSeparator />}
          {children}
        </div>

        {search && searchOpen && (
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Input
              ref={inputRef}
              type="search"
              aria-label={search.placeholder}
              placeholder={search.placeholder}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') closeSearch(); }}
              // #2407 step 4: the toolbar draws its own ×, so hide the
              // browser's native search-cancel glyph to avoid two of them.
              className="h-8 pr-8 [&::-webkit-search-cancel-button]:appearance-none"
            />
            <button
              type="button"
              aria-label={t('toolbar.closeSearch')}
              onClick={closeSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-stevens-ink-3 hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {end && (
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{end}</div>
        )}
      </div>
    </TooltipProvider>
  );
}
